import { useTranslation } from 'react-i18next';

type Card = { key: string; icon: string; tint: string; color: string };

/** Six reasons, each with a small data visual. Copy in home.whyGeofleet.items. Numbers are the agreed site-wide ones. */
const CARDS: Card[] = [
  { key: 'dispatch', icon: 'ri-flashlight-line', tint: '#FFEDD5', color: '#F97316' },
  { key: 'onTime', icon: 'ri-focus-3-line', tint: '#DCFCE7', color: '#16A34A' },
  { key: 'cost', icon: 'ri-funds-line', tint: '#DBEAFE', color: '#2563EB' },
  { key: 'reliability', icon: 'ri-shield-check-line', tint: '#EDE9FE', color: '#7C3AED' },
  { key: 'integrations', icon: 'ri-plug-line', tint: '#E0F2FE', color: '#0284C7' },
  { key: 'global', icon: 'ri-global-line', tint: '#FCE7F3', color: '#DB2777' },
];

function Ring({ pct, color }: { pct: number; color: string }) {
  const r = 26;
  const circ = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16" aria-hidden>
      <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${(circ * pct) / 100} ${circ}`}
        transform="rotate(-90 32 32)"
      />
    </svg>
  );
}

export default function WhyGeofleet() {
  const { t } = useTranslation();

  const visual = (key: string, color: string) => {
    switch (key) {
      case 'dispatch':
        return (
          <div className="flex items-center justify-between gap-3">
            {['order', 'matched', 'accepted'].map((step, i) => (
              <div key={step} className="flex flex-1 items-center gap-2">
                <div className="flex flex-col items-center">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full text-sm text-white" style={{ backgroundColor: color }}>
                    <i className={['ri-inbox-line', 'ri-robot-line', 'ri-check-line'][i]} aria-hidden />
                  </span>
                  <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{t(`home.whyGeofleet.labels.${step}`)}</span>
                </div>
                {i < 2 && <span className="mb-5 h-0.5 flex-1 rounded-full bg-gray-200" aria-hidden />}
              </div>
            ))}
          </div>
        );
      case 'onTime':
        return (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-3xl font-bold leading-none" style={{ color }}>
                95%
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t('home.whyGeofleet.labels.onTimeRate')}</p>
            </div>
            <Ring pct={95} color={color} />
          </div>
        );
      case 'cost':
        return (
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-3xl font-bold leading-none" style={{ color }}>
                20–30%
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t('home.whyGeofleet.labels.lowerCost')}</p>
            </div>
            <div className="flex items-end gap-2" aria-hidden>
              <div className="flex flex-col items-center gap-1">
                <span className="block h-14 w-9 rounded-md bg-gray-300" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{t('home.whyGeofleet.labels.manual')}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <span className="block h-10 w-9 rounded-md" style={{ backgroundColor: color }} />
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color }}>
                  Geofleet
                </span>
              </div>
            </div>
          </div>
        );
      case 'reliability':
        return (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-3xl font-bold leading-none" style={{ color }}>
                99.99%
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t('home.whyGeofleet.labels.uptime')}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              {['SOC 2', 'ISO 27001'].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-bold text-gray-700">
                  <i className="ri-shield-check-fill" style={{ color }} aria-hidden />
                  {b}
                </span>
              ))}
            </div>
          </div>
        );
      case 'integrations':
        return (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-3xl font-bold leading-none" style={{ color }}>
                50+
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t('home.whyGeofleet.labels.integrations')}</p>
            </div>
            <div className="flex max-w-[180px] flex-wrap justify-end gap-1.5">
              {['Shopify', 'WhatsApp', 'Stripe', 'Toast', 'Webhooks', 'API'].map((n) => (
                <span key={n} className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                  {n}
                </span>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-3xl font-bold leading-none" style={{ color }}>
                20+
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{t('home.whyGeofleet.labels.countries')}</p>
            </div>
            <div className="flex gap-1.5">
              {['EN', 'ES', 'FR', 'AR'].map((l) => (
                <span key={l} className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-[10px] font-bold text-gray-700">
                  {l}
                </span>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <section
      className="relative border-t border-gray-100 bg-white py-14 lg:py-16"
      style={{ backgroundImage: 'linear-gradient(to right, rgba(15,23,42,0.035) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.035) 1px, transparent 1px)', backgroundSize: '48px 48px' }}
      aria-labelledby="why-geofleet-title"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.whyGeofleet.eyebrow')}</p>
          <h2 id="why-geofleet-title" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {t('home.whyGeofleet.title')} <span className="block text-blue-600">{t('home.whyGeofleet.titleHighlight')}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-gray-600">{t('home.whyGeofleet.subtitle')}</p>
        </div>

        <ul className="m-0 grid list-none gap-5 p-0 md:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((card) => (
            <li key={card.key} className="flex flex-col rounded-3xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-lg sm:p-7">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl" style={{ backgroundColor: card.tint, color: card.color }}>
                  <i className={card.icon} aria-hidden />
                </span>
                <h3 className="text-lg font-bold text-gray-900">{t(`home.whyGeofleet.items.${card.key}.title`)}</h3>
              </div>
              <p className="mt-4 text-[15px] leading-relaxed text-gray-600">{t(`home.whyGeofleet.items.${card.key}.body`)}</p>
              <div className="mt-auto pt-6">
                <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-gray-100">{visual(card.key, card.color)}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
